/**
 * application-form — pure audience / effective-visibility logic and the merit
 * judge projection (application-form-builder spec, task 8.1).
 *
 * This module holds the I/O-free heart of Requirement 22 (audience visibility)
 * and the Judge half of Requirement 21 (per-page approval details surfaced to
 * judges). It owns three decisions:
 *
 *   1. `isInAudience` — the Audience_Set membership half of Effective_Visibility
 *      (Req 22.2–22.5). An empty Audience_Set means "no audience" — the element
 *      is shown to nobody.
 *   2. `isEffectivelyVisible` — Effective_Visibility = Visibility_Scope (division
 *      membership, decided upstream) AND Audience_Set membership (Req 22.6).
 *   3. `projectForJudge` — walk a captured `FormVersionSnapshot`, keep only the
 *      pages/fields whose Effective_Visibility for the `JUDGE` viewer is true,
 *      resolve Bound_Fields from their canonical sources, append exactly the
 *      director-configured `Approval_Details` fields, and emit the existing
 *      `ApplicationSection[]` contract the merit `ContestantApplicationView`
 *      already renders (Req 21.8, 22.4, 22.5, 22.6, 22.8). PII is never widened:
 *      identity-bound (Contact/identity) values are still gated by `includePii`,
 *      mirroring the `merit-judging` `toJudgeContestant(..., includePii)`
 *      precedent.
 *
 * Like its sibling modules (`fieldValidation.ts`, `formVersion.ts`,
 * `pageApproval.ts`) it contains NO AWS SDK calls and NO I/O. Every exported
 * function is total and pure — the same code runs in the `application-form`
 * Lambda (`jest` + `fast-check`, task 8.2) and is re-used by the frontend
 * (`vitest`), exactly the `division-registry/visibility.ts` /
 * `person-service/personLogic.ts` precedent.
 *
 * Validates: Requirements 22.2, 22.3, 22.4, 22.5, 22.6, 22.8, 21.8
 */

import type {
  Audience,
  FormFieldDef,
  FormVersionSnapshot,
  ResponseValue,
} from './fieldValidation';
import type { ApprovalDetails } from './pageApproval';

// Re-export the Audience vocabulary from its base module so callers can import
// the audience type alongside the audience logic that consumes it.
export type { Audience };

// ---------------------------------------------------------------------------
// Canonical-source read model + bound-field resolution
//
// NOTE (consolidation): `CanonicalSources`, `ResolvedBoundValue`, and
// `resolveBoundField` are the canonical responsibility of `boundField.ts`
// (spec task 4.1), which is landing in parallel. At the time this module was
// authored `boundField.ts` was not yet on disk, so a MINIMAL local copy of the
// read model and a focused resolver live here to keep task 8.1 self-contained
// and type-correct. When `boundField.ts` lands, replace these local
// declarations with `import { CanonicalSources, resolveBoundField } from
// './boundField'` and delete the local copies — the shapes are intentionally
// identical to the design's `boundField.ts` contract.
// ---------------------------------------------------------------------------

/** The current contact address shape promoted onto Person (mirrors `contactIntake.PersonAddress`). */
export interface PersonAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

/** A minimal uploaded-document view used to resolve a Document slot Bound_Field. */
export interface DocumentRef {
  catalogEntryId?: string;
  fileName: string;
}

/**
 * The canonical entities a Bound_Field resolves from for rendering / review /
 * judge projection (Req 9.1, 10.6). Bound_Fields store NO value in the
 * Application_Response; their displayed value is always read from here.
 */
export interface CanonicalSources {
  person?: {
    legalFirstName: string;
    legalLastName: string;
    primaryEmail: string;
    mobilePhone?: string;
    dateOfBirth?: string;
    address?: PersonAddress;
  };
  participation?: {
    divisionKey?: string;
    approvedTitle?: string;
  };
  documents?: DocumentRef[];
  titlePreferences?: string[];
}

/** The resolved display of a Bound_Field, plus whether it is identity PII. */
export interface ResolvedBoundValue {
  /** Pre-formatted display string read from the field's canonical source. */
  display: string;
  /**
   * Whether the resolved value is identity PII (a Contact/identity binding),
   * which must stay gated behind `includePii` and never be widened (Req 22.8).
   */
  isPii: boolean;
}

/**
 * Render a `PersonAddress` to a single-line display string, skipping blanks.
 */
function formatAddress(address: PersonAddress | undefined): string {
  if (!address) {
    return '';
  }
  return [address.street, address.city, address.state, address.zip]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0)
    .join(', ');
}

/**
 * Resolve a Bound_Field to its display value from the canonical sources
 * (Req 10.6). Pure and total: an unresolved source yields an empty display
 * rather than a wrong value. `isPii` is true only for the Contact/identity
 * binding, which the judge projection gates behind `includePii` (Req 22.8).
 *
 * NOTE (consolidation): this is the minimal local stand-in for
 * `boundField.ts#resolveBoundField` — see the module note above.
 */
export function resolveBoundField(
  field: FormFieldDef,
  sources: CanonicalSources,
): ResolvedBoundValue {
  switch (field.bindingTarget) {
    case 'DIVISION':
      return { display: (sources.participation?.divisionKey ?? '').trim(), isPii: false };

    case 'TITLE_PREFERENCES':
      return {
        display: (sources.titlePreferences ?? [])
          .map((t) => (typeof t === 'string' ? t.trim() : ''))
          .filter((t) => t.length > 0)
          .join(', '),
        isPii: false,
      };

    case 'DOCUMENT_SLOT':
      return {
        display: (sources.documents ?? [])
          .map((d) => (typeof d?.fileName === 'string' ? d.fileName.trim() : ''))
          .filter((name) => name.length > 0)
          .join(', '),
        isPii: false,
      };

    case 'CONTACT_IDENTITY': {
      const person = sources.person;
      if (!person) {
        return { display: '', isPii: true };
      }
      const display = [
        `${person.legalFirstName ?? ''} ${person.legalLastName ?? ''}`.trim(),
        (person.primaryEmail ?? '').trim(),
        (person.mobilePhone ?? '').trim(),
        (person.dateOfBirth ?? '').trim(),
        formatAddress(person.address),
      ]
        .filter((part) => part.length > 0)
        .join(' · ');
      return { display, isPii: true };
    }

    default:
      // A field with no/unknown binding target resolves to no value.
      return { display: '', isPii: false };
  }
}

// ---------------------------------------------------------------------------
// The merit ApplicationSection[] contract
//
// NOTE: the backend has no shared `ApplicationSection` type — the contract is
// owned by the frontend merit client (`frontend/src/lib/merit/client/types.ts`)
// and crosses the wire as AWSJSON from `getJudgeProjection`. These local shapes
// mirror that contract field-for-field so the projection emits exactly what the
// existing `ContestantApplicationView` already renders.
// ---------------------------------------------------------------------------

/** One labeled value in a contestant's projected application. */
export interface ApplicationField {
  label: string;
  /** Pre-formatted display string (multi-line allowed); blank values are omitted by the view. */
  value: string;
}

/** An ordered, titled group of {@link ApplicationField}s — typically one Form_Page. */
export interface ApplicationSection {
  title: string;
  fields: ApplicationField[];
}

// ---------------------------------------------------------------------------
// Audience membership (Req 22.2–22.5)
// ---------------------------------------------------------------------------

/**
 * Whether an element carrying `audienceSet` is presented to `viewer` (Req
 * 22.2–22.5). An empty (or absent) Audience_Set means the element is shown to
 * NO audience — there is no implicit "all audiences" default at this layer (the
 * snapshot already applied any `DEFAULT_AUDIENCE_SET` when it was captured).
 * Pure and total.
 */
export function isInAudience(
  audienceSet: readonly Audience[] | undefined,
  viewer: Audience,
): boolean {
  if (!audienceSet) {
    return false;
  }
  return audienceSet.includes(viewer);
}

/**
 * Effective_Visibility for a viewer = Visibility_Scope (division membership,
 * decided upstream by `formStructure.ts#isFieldVisibleForDivision` /
 * `division-registry/visibility.ts`) AND Audience_Set membership (Req 22.6).
 * The division decision is passed in as `divisionVisible` so this function owns
 * only the audience conjunction and never re-implements the division rule.
 * Pure and total.
 */
export function isEffectivelyVisible(
  divisionVisible: boolean,
  audienceSet: readonly Audience[] | undefined,
  viewer: Audience,
): boolean {
  return divisionVisible && isInAudience(audienceSet, viewer);
}

// ---------------------------------------------------------------------------
// Division visibility (applicant/viewer-scoped) — local mirror
// ---------------------------------------------------------------------------

/**
 * Whether a `visibleDivisionKeys` selection means "all divisions" — null,
 * undefined, or only-blank entries (the legacy `BOTH` semantics). Mirrors
 * `fieldValidation.ts` / `division-registry/visibility.ts`.
 */
function isAllDivisionsVisibility(
  visibleDivisionKeys: readonly string[] | null | undefined,
): boolean {
  if (visibleDivisionKeys == null) {
    return true;
  }
  return visibleDivisionKeys.every((k) => typeof k !== 'string' || k.trim() === '');
}

/**
 * Whether an element carrying `visibleDivisionKeys` is division-visible to a
 * viewer scoped to `viewerDivisionKey`. A null/empty scope is visible to all;
 * a populated scope is visible iff it contains the viewer's key (Req 4.2, 4.3).
 * A `null` viewer division (an unscoped viewer, e.g. an admin) sees all-division
 * elements but not division-restricted ones.
 */
function isDivisionVisibleToViewer(
  visibleDivisionKeys: readonly string[] | null | undefined,
  viewerDivisionKey: string | null,
): boolean {
  if (isAllDivisionsVisibility(visibleDivisionKeys)) {
    return true;
  }
  if (viewerDivisionKey === null) {
    return false;
  }
  return (visibleDivisionKeys as readonly string[]).some(
    (k) => typeof k === 'string' && k.trim() !== '' && k === viewerDivisionKey,
  );
}

// ---------------------------------------------------------------------------
// Value formatting for the projection
// ---------------------------------------------------------------------------

/**
 * Format a stored Custom_Field Response_Value into the pre-formatted display
 * string the `ApplicationSection[]` contract carries. An absent value yields an
 * empty string (the view omits blank fields).
 */
function formatResponseValue(value: ResponseValue | undefined): string {
  if (value === undefined) {
    return '';
  }
  switch (value.kind) {
    case 'text':
    case 'date':
      return value.value;
    case 'number':
      return String(value.value);
    case 'boolean':
      return value.value ? 'Yes' : 'No';
    case 'list':
      return value.value.join(', ');
    case 'files':
      return value.value.map((f) => f.fileName).join(', ');
  }
}

// ---------------------------------------------------------------------------
// projectForJudge (Req 21.8, 22.4, 22.5, 22.6, 22.8)
// ---------------------------------------------------------------------------

/**
 * Project a captured response into the merit judge `ApplicationSection[]` shape.
 *
 * The walk preserves the snapshot's page/field order and keeps ONLY the content
 * whose Effective_Visibility for the `JUDGE` viewer is true — i.e. a page (and a
 * field within it) is included iff its Visibility_Scope contains the judge's
 * division AND its Audience_Set includes `JUDGE` (Req 22.4, 22.5, 22.6). A page
 * whose own audience excludes Judge, or whose division scope excludes the
 * viewer, contributes nothing.
 *
 * Within an included page:
 *   - a Custom_Field's value is read from `values` and formatted for display;
 *   - a Bound_Field's value is resolved from `sources` (Req 10.6) — never from a
 *     stored copy — and an identity (Contact/identity) value is OMITTED unless
 *     `includePii` is true, so PII is never widened (Req 22.8);
 *   - the director-configured `Approval_Details.fields` for that page (if any)
 *     are appended exactly as configured-visible to judges (Req 21.7, 21.8).
 *
 * Approval-detail fields whose page is not otherwise judge-visible are surfaced
 * in a dedicated trailing "<page title> — Approval Details" section, because the
 * director configured those specific detail fields visible to judges (Req 21.8).
 *
 * A field that resolves to a blank display string is still emitted (with an
 * empty `value`); the consuming view drops blanks. Empty sections (no fields)
 * are omitted. Pure and total; no argument is mutated.
 */
export function projectForJudge(
  snapshot: FormVersionSnapshot,
  values: Record<string, ResponseValue>,
  sources: CanonicalSources,
  approvals: ReadonlyArray<{ formPageId: string; details?: ApprovalDetails }>,
  opts: { includePii: boolean; viewerDivisionKey: string | null },
): ApplicationSection[] {
  return projectForAudience('JUDGE', snapshot, values, sources, approvals, opts);
}

/**
 * Project a captured response into the DIRECTOR review `ApplicationSection[]`
 * shape (application-form-builder Req 9.1, 9.3). Same walk as
 * {@link projectForJudge} but for the `DIRECTOR` audience: the director sees the
 * content whose Audience_Set includes `DIRECTOR` (the documented baseline
 * default is `['APPLICANT','DIRECTOR']`, so ordinary applicant-entered content
 * is director-visible). A director reviewing an applicant is authorized for full
 * identity, so callers pass `includePii: true`. Pure and total.
 */
export function projectForReview(
  snapshot: FormVersionSnapshot,
  values: Record<string, ResponseValue>,
  sources: CanonicalSources,
  approvals: ReadonlyArray<{ formPageId: string; details?: ApprovalDetails }>,
  opts: { includePii: boolean; viewerDivisionKey: string | null },
): ApplicationSection[] {
  return projectForAudience('DIRECTOR', snapshot, values, sources, approvals, opts);
}

/**
 * The shared, audience-parametrized projection core behind
 * {@link projectForJudge} and {@link projectForReview}. Preserves the snapshot's
 * page/field order and keeps ONLY the content whose Effective_Visibility for
 * `audience` is true (its Visibility_Scope contains the viewer's division AND
 * its Audience_Set includes `audience`). Within an included page a Custom_Field
 * reads from `values`; a Bound_Field resolves from `sources` (never a stored
 * copy) and an identity value is withheld unless `includePii`; the
 * director-configured `Approval_Details.fields` for the page are appended.
 */
export function projectForAudience(
  audience: Audience,
  snapshot: FormVersionSnapshot,
  values: Record<string, ResponseValue>,
  sources: CanonicalSources,
  approvals: ReadonlyArray<{ formPageId: string; details?: ApprovalDetails }>,
  opts: { includePii: boolean; viewerDivisionKey: string | null },
): ApplicationSection[] {
  const sections: ApplicationSection[] = [];

  // Index the director-configured approval-detail fields by page, so each page
  // can append its configured-visible details and we can later surface details
  // for pages that are not otherwise visible to the audience.
  const approvalsByPage = new Map<string, ApprovalDetails | undefined>();
  for (const approval of approvals) {
    // Last write wins for a duplicated pageId; details may be undefined.
    approvalsByPage.set(approval.formPageId, approval.details);
  }

  for (const page of snapshot.pages) {
    const pageDivisionVisible = isDivisionVisibleToViewer(
      page.visibleDivisionKeys,
      opts.viewerDivisionKey,
    );
    const pageVisible = isEffectivelyVisible(pageDivisionVisible, page.audienceSet, audience);

    const fields: ApplicationField[] = [];

    if (pageVisible) {
      for (const field of page.fields) {
        if (!isFieldVisibleToAudience(field, pageDivisionVisible, opts.viewerDivisionKey, audience)) {
          continue;
        }

        if (field.fieldCategory === 'BOUND') {
          const resolved = resolveBoundField(field, sources);
          // Never widen PII: an identity-bound value is withheld unless includePii.
          if (resolved.isPii && !opts.includePii) {
            continue;
          }
          fields.push({ label: field.label, value: resolved.display });
        } else {
          fields.push({ label: field.label, value: formatResponseValue(values[field.id]) });
        }
      }
    }

    // Append the director-configured Approval_Details fields for this page.
    const details = approvalsByPage.get(page.id);
    const approvalFields = toApprovalFields(details);

    if (pageVisible) {
      fields.push(...approvalFields);
      if (fields.length > 0) {
        sections.push({ title: page.title, fields });
      }
    } else if (approvalFields.length > 0) {
      // The page itself is not visible to this audience, but the director
      // configured these approval detail fields visible (Req 21.8) — surface them.
      sections.push({ title: `${page.title} — Approval Details`, fields: approvalFields });
    }
  }

  return sections;
}

/**
 * Whether a field within an already-audience-visible page is itself visible to
 * `audience`: its own division scope contains the viewer AND its Audience_Set
 * includes `audience` (Req 22.5, 22.6). The containing page's division
 * visibility is required too, mirroring `formStructure.ts#isFieldVisibleForDivision`
 * (a field is not visible when its page is not).
 */
function isFieldVisibleToAudience(
  field: FormFieldDef,
  pageDivisionVisible: boolean,
  viewerDivisionKey: string | null,
  audience: Audience,
): boolean {
  const fieldDivisionVisible =
    pageDivisionVisible &&
    isDivisionVisibleToViewer(field.visibleDivisionKeys, viewerDivisionKey);
  return isEffectivelyVisible(fieldDivisionVisible, field.audienceSet, audience);
}

/**
 * Flatten the director-configured `Approval_Details.fields` into projected
 * application fields. Only the structured `fields` map (the director's
 * configured-visible detail entries) is projected; the workflow metadata
 * (`approvedBy`, `decidedAt`, `note`) is intentionally NOT surfaced to judges.
 */
function toApprovalFields(details: ApprovalDetails | undefined): ApplicationField[] {
  if (!details || !details.fields) {
    return [];
  }
  return Object.entries(details.fields).map(([label, value]) => ({
    label,
    value: typeof value === 'string' ? value : String(value),
  }));
}
